import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/marketing/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/marketing/components/ui/dialog";
import { Input } from "@/marketing/components/ui/input";
import type { CampaignOperationsBatches, CampaignOperationsExpectedValues } from "@/marketing/models/campaignOperations";
import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";
import { CampaignBatches } from "./campaignBatches";

type BatchDuePhase = "shipping" | "posting";

interface CampaignBatchManagerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaign: InfluencerCampaign;
  creatorCounts: Record<string, number>;
  onChange: (batches: CampaignOperationsBatches, expected: CampaignOperationsExpectedValues) => Promise<void>;
}

// Text being edited plus the stored value it replaces, which the save sends as its expected value.
interface BatchFieldDraft {
  value: string;
  original: string | null;
}

const DUE_PHASES: { phase: BatchDuePhase; label: string }[] = [
  { phase: "shipping", label: "Shipping due" },
  { phase: "posting", label: "Posting due" },
];

/** Creates, renames, dates and deletes the batches of one campaign; each edit saves on its own with a field-level lock. */
export function CampaignBatchManager({ open, onOpenChange, campaign, creatorCounts, onChange }: CampaignBatchManagerProps) {
  const [drafts, setDrafts] = useState<Record<string, BatchFieldDraft>>({});
  const [savingKeys, setSavingKeys] = useState<ReadonlySet<string>>(new Set());
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);
  const [addingBatch, setAddingBatch] = useState(false);
  const storedBatches = campaign.operations?.batches ?? {};
  const batches = CampaignBatches.list(campaign);

  // One add at a time: the next name is only right once the previous batch has reloaded.
  const addBatch = () => {
    if (addingBatch) return;
    const batchId = CampaignBatches.newBatchId();
    setAddingBatch(true);
    void onChange({ [batchId]: { name: CampaignBatches.nextName(campaign) } }, { [`batches.${batchId}`]: null }).finally(() => setAddingBatch(false));
  };

  // The value shown when editing began is what the save expects, even if a reload changes it meanwhile.
  const setDraft = (draftKey: string, value: string, storedValue: string | null) =>
    setDrafts((current) => ({ ...current, [draftKey]: { value, original: draftKey in current ? (current[draftKey] as BatchFieldDraft).original : storedValue } }));

  const clearDraft = (draftKey: string) =>
    setDrafts((current) => {
      const { [draftKey]: _settled, ...rest } = current;
      return rest;
    });

  const setSaving = (draftKey: string, saving: boolean) =>
    setSavingKeys((current) => {
      const next = new Set(current);
      if (saving) next.add(draftKey);
      else next.delete(draftKey);
      return next;
    });

  // The field stays read-only until its save settles and the batch reloads, so the editor cannot race their own save.
  const saveDraft = (draftKey: string, toChange: (value: string | null) => CampaignOperationsBatches, expectedPath: string) => {
    const draft = drafts[draftKey];
    if (!draft || savingKeys.has(draftKey)) return;
    const value = draft.value.trim() || null;
    if (value === draft.original) {
      clearDraft(draftKey);
      return;
    }
    setSaving(draftKey, true);
    void onChange(toChange(value), { [expectedPath]: draft.original }).finally(() => {
      clearDraft(draftKey);
      setSaving(draftKey, false);
    });
  };

  const saveName = (batchId: string) => saveDraft(`${batchId}:name`, (name) => ({ [batchId]: { name } }), `batches.${batchId}.name`);

  const saveDueDate = (batchId: string, phase: BatchDuePhase) =>
    saveDraft(`${batchId}:${phase}`, (dueDate) => ({ [batchId]: { phases: { [phase]: { dueDate } } } }), `batches.${batchId}.phases.${phase}.dueDate`);

  const deleteBatch = (batchId: string) => {
    setConfirmingDelete(null);
    void onChange({ [batchId]: null }, { [`batches.${batchId}`]: storedBatches[batchId] ?? null });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tw-max-w-3xl">
        <DialogHeader>
          <DialogTitle>Batches</DialogTitle>
          <DialogDescription>Shipping and posting waves of this campaign. Move creators into a batch from the influencer list.</DialogDescription>
        </DialogHeader>
        <table className="tw-w-full tw-text-sm">
          <thead>
            <tr className="tw-text-left tw-text-[11px] tw-uppercase tw-tracking-wider tw-text-muted-foreground">
              <th className="tw-py-2 tw-pr-2 tw-font-semibold">Name</th>
              <th className="tw-py-2 tw-pr-2 tw-font-semibold">Creators</th>
              {DUE_PHASES.map(({ phase, label }) => (
                <th key={phase} className="tw-py-2 tw-pr-2 tw-font-semibold">
                  {label}
                </th>
              ))}
              <th className="tw-py-2" />
            </tr>
          </thead>
          <tbody>
            {batches.length === 0 && (
              <tr>
                <td colSpan={5} className="tw-py-6 tw-text-center tw-text-muted-foreground">
                  No batches yet.
                </td>
              </tr>
            )}
            {batches.map(({ batchId, name }) => {
              const creatorCount = creatorCounts[batchId] ?? 0;
              return (
                <tr key={batchId} aria-label={name} className="tw-border-t tw-border-border">
                  <td className="tw-py-2 tw-pr-2">
                    <Input
                      aria-label="Batch name"
                      className="tw-h-8"
                      value={drafts[`${batchId}:name`]?.value ?? storedBatches[batchId]?.name ?? ""}
                      readOnly={savingKeys.has(`${batchId}:name`)}
                      onChange={(event) => setDraft(`${batchId}:name`, event.target.value, storedBatches[batchId]?.name ?? null)}
                      onBlur={() => saveName(batchId)}
                    />
                  </td>
                  <td className="tw-whitespace-nowrap tw-py-2 tw-pr-2 tw-text-muted-foreground">
                    {creatorCount} creator{creatorCount === 1 ? "" : "s"}
                  </td>
                  {DUE_PHASES.map(({ phase, label }) => (
                    <td key={phase} className="tw-py-2 tw-pr-2">
                      <Input
                        type="date"
                        aria-label={label}
                        className="tw-h-8"
                        value={drafts[`${batchId}:${phase}`]?.value ?? storedBatches[batchId]?.phases?.[phase]?.dueDate ?? ""}
                        readOnly={savingKeys.has(`${batchId}:${phase}`)}
                        onChange={(event) => setDraft(`${batchId}:${phase}`, event.target.value, storedBatches[batchId]?.phases?.[phase]?.dueDate ?? null)}
                        // A half-cleared date reads as empty; put the stored date back instead of deleting it.
                        onBlur={(event) => (event.currentTarget.validity.badInput ? clearDraft(`${batchId}:${phase}`) : saveDueDate(batchId, phase))}
                      />
                    </td>
                  ))}
                  <td className="tw-whitespace-nowrap tw-py-2 tw-text-right">
                    {confirmingDelete === batchId ? (
                      <span className="tw-inline-flex tw-gap-1">
                        <Button variant="destructive" size="sm" className="tw-h-8" onClick={() => deleteBatch(batchId)}>
                          Confirm delete, unassigns {creatorCount} creator{creatorCount === 1 ? "" : "s"}
                        </Button>
                        <Button variant="ghost" size="sm" className="tw-h-8" onClick={() => setConfirmingDelete(null)}>
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <Button variant="ghost" size="sm" className="tw-h-8" aria-label={`Delete ${name}`} onClick={() => setConfirmingDelete(batchId)}>
                        <Trash2 className="tw-h-3.5 tw-w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div>
          <Button variant="outline" size="sm" className="tw-gap-1.5" disabled={addingBatch} onClick={addBatch}>
            <Plus className="tw-h-3.5 tw-w-3.5" />
            Add batch
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
